'use client';

import {
	type AccountInfo,
	type AuthenticationResult,
	BrowserAuthError,
	InteractionRequiredAuthError,
	InteractionStatus,
	InteractionType,
	type IPublicClientApplication,
	type PopupRequest,
	type RedirectRequest,
	type SilentRequest,
} from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { isAfter } from '@dte/otw-utils-dates/src/compare/isAfter';
import type { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { addMinutes, minutesToMilliseconds, secondsToMilliseconds } from 'date-fns';
import { useCallback, useEffect, useMemo } from 'react';
import useSWR, { type SWRConfiguration } from 'swr';
import { getLoginMethod, useRedirectRequest } from '../../common/util/employeeAuthConfig';
import { logDebug, logError, logException, logInfo, logWarning } from '../../common/util/logging/writeLog';
import { useAppInsights } from '../../components/AppInsightsWrapper/AppInsightsWrapper';
import { getTokenExpiration } from './isGoodToken';
import { useMsalAccount } from './useMsalAccount';

function tokenExpiresSoon(token: string | undefined, minutes = 10): boolean {
	if (!token) {
		console.debug('Token is undefined');
		return true;
	}

	const expiryDate = getTokenExpiration(token);
	if (!expiryDate) {
		console.debug("Token doesn't have an expiry date");
		return true;
	}

	// Token expires within the next X minutes
	const thresholdDate = addMinutes(new Date(), minutes);
	if (isAfter(thresholdDate, expiryDate)) {
		console.debug('Token expires soon, need to refresh');
		return true;
	}

	return false;
}

async function handleLoginError(
	appInsights: ReactPlugin,
	instance: IPublicClientApplication,
	account: AccountInfo | null,
	error: unknown,
	redirectRequest: RedirectRequest | PopupRequest,
	forceManualLogin?: boolean,
): Promise<AuthenticationResult | undefined> {
	let retryError = false;
	let forceManualLoginOnRetry = forceManualLogin;

	if (error instanceof InteractionRequiredAuthError) {
		logWarning(appInsights, 'Interaction required - do a manual login');
		retryError = true;
		forceManualLoginOnRetry = true;
	}

	if (error instanceof BrowserAuthError) {
		const browserAuthError = error;
		if (browserAuthError?.errorMessage?.includes('timeout')) {
			logWarning(appInsights, 'Login timed out, retry');
			retryError = true;
		}
	}

	// If we weren't already trying a manual login, go ahead and try again
	if (retryError) {
		return fetchAuthenticationResult(
			appInsights,
			instance,
			account,
			redirectRequest,
			true,
			forceManualLoginOnRetry,
		);
	}

	return undefined;
}

export async function manualLogin(
	appInsights: ReactPlugin,
	instance: IPublicClientApplication,
	redirectRequest: RedirectRequest | PopupRequest,
): Promise<AuthenticationResult | undefined> {
	const loginMethod = getLoginMethod();

	logInfo(appInsights, `Attempt to acquire token manually, method: [ ${loginMethod} ]`);

	if (loginMethod === InteractionType.Redirect) {
		await instance.acquireTokenRedirect(redirectRequest);
		return undefined;
	}

	if (loginMethod === InteractionType.Popup) {
		return instance.acquireTokenPopup(redirectRequest);
	}

	return undefined;
}

async function handleLogin(
	appInsights: ReactPlugin,
	instance: IPublicClientApplication,
	tokenRequest: SilentRequest,
	redirectRequest: RedirectRequest | PopupRequest,
	forceManualLogin?: boolean,
): Promise<AuthenticationResult | undefined> {
	if (forceManualLogin) {
		// Use a manual process like popup or redirect to get the token
		return manualLogin(appInsights, instance, redirectRequest);
	}

	return instance.acquireTokenSilent(tokenRequest);
}

// TODO: logic is somewhat convoluted because MSAL doesn't properly check for expiry of id tokens
//
// TODO: the suggested answer here is to use access tokens instead
// See:
// https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/4206
async function fetchAuthenticationResult(
	appInsights: ReactPlugin,
	instance: IPublicClientApplication,
	account: AccountInfo | null,
	redirectRequest: RedirectRequest | PopupRequest,
	forceRefresh?: boolean,
	forceManualLogin?: boolean,
): Promise<AuthenticationResult | undefined> {
	let useForceRefresh = forceRefresh;

	// Set refresh initially based on the age of the current cached
	let needsRefresh = tokenExpiresSoon(account?.idToken);
	if (needsRefresh) {
		useForceRefresh = true;
	}

	const tokenRequest: SilentRequest = {
		scopes: ['openid'],
		account: account ?? undefined,
		forceRefresh: useForceRefresh,
	};

	let result: AuthenticationResult | undefined;

	try {
		logDebug(appInsights, `Attempting to acquire token silently, force refresh: ${useForceRefresh}`);
		result = await handleLogin(appInsights, instance, tokenRequest, redirectRequest, forceManualLogin);
	} catch (error) {
		// If we're not already trying a manual login
		if (!forceManualLogin) {
			return handleLoginError(appInsights, instance, account, error, redirectRequest, forceManualLogin);
		}
		logError(appInsights, 'Manual attempt to retrieve token failed due to error');
		logException(appInsights, error);
	}

	// Make sure the result contains a token that isn't about to expire
	needsRefresh = tokenExpiresSoon(result?.idToken);
	if (needsRefresh) {
		// Result isn't valid
		result = undefined;

		// Fall back on force refresh if needed
		if (!useForceRefresh) {
			logWarning(appInsights, 'Try again to acquire token, this time forcing a manual login');
			return fetchAuthenticationResult(appInsights, instance, account, redirectRequest, true, forceManualLogin);
		}

		// Fall back on manual login next
		if (!forceManualLogin) {
			logWarning(appInsights, 'Manual attempt to retrieve token failed, token was undefined');
			return fetchAuthenticationResult(appInsights, instance, account, redirectRequest, true, true);
		}

		logError(appInsights, 'Unable to retrive token from either force refresh or manual login');
	}

	return result;
}

const swrOptions: SWRConfiguration = {
	// Don't try to grab again on focus for at least 5 minutes
	focusThrottleInterval: minutesToMilliseconds(5),
	// If multiple requests come in within the same 15 seconds, make sure to group them together
	dedupingInterval: secondsToMilliseconds(15),
	// How long to trigger slow loading indicator
	loadingTimeout: secondsToMilliseconds(3),

	suspense: false,
	fallbackData: undefined,
	// TODO: maybe we can use suspense with React 19?
};

// TODO: we may be able to use the newer useMsalAuthentication hook to replace much of this

export function useAuthenticationResult(): [
	AuthenticationResult | undefined,
	() => Promise<AuthenticationResult | undefined>,
] {
	const appInsights = useAppInsights();

	// Attemp silent login first
	const { instance, inProgress } = useMsal();
	const account = useMsalAccount();

	const redirectRequest = useRedirectRequest();

	const readyToFetch = useMemo(() => {
		if (inProgress !== InteractionStatus.None) {
			// Login in progress
			return false;
		}
		// App insights isn't setup yet
		if (!appInsights) {
			return false;
		}
		// Redirect request isn't setup yet
		if (!redirectRequest) {
			return false;
		}
		// No account yet
		if (!account) {
			return false;
		}
		return true;
	}, [appInsights, account, inProgress, redirectRequest]);

	const fetcher = useCallback(async () => {
		return fetchAuthenticationResult(appInsights, instance, account, redirectRequest);
	}, [appInsights, instance, account, redirectRequest]);

	// Unique key to identify this input element
	const key = readyToFetch ? 'msalAuth' : undefined;
	const {
		data: authenticationResult,
		mutate,
		error,
	} = useSWR<AuthenticationResult | undefined>(key, fetcher, swrOptions);

	useEffect(() => {
		if (error) {
			logError(appInsights, 'Error fetching authentication result');
			logException(appInsights, error);
		}
	}, [appInsights, error]);

	return [authenticationResult, mutate];
}
